import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { IOption } from '../../../../shared/components/forms/search-input/BpSearchInput'
import { toTextOptions } from '../../../../shared/lib/helpers/ToTextOptions'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { Controller, useForm } from 'react-hook-form'
import React, { useEffect } from 'react'
import { FilterDrop } from '../../../../shared/components/cards/filter-drop/FilterDrop'
import { BpDatePicker } from '../../../../shared/components/forms/datepicker/BpDatePicker'
import { setCanCloseDropdown } from '../../../../app/store/reducers/AppSlice'
import moment from 'moment/moment'
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_RANGE_FORMAT,
} from '../../../../shared/consts/DefaultRangeFormat.const'
import { selectDetailedInfoFilters, setDetailedInfoFilter } from '../../model/domainOverviewSlice'
import { IDetailedInfoFilter } from '../../model/types/DetailedInfoFilter.interface'
import {
  IWithSearchParamsProps,
  withSearchParams,
} from '../../../../shared/lib/hoc/WithSearchParams'
import { Switch } from 'antd'
import { useTranslation } from 'react-i18next'
import { AddProjectButton } from '../../../../shared/components/buttons/add-project-button/AddProjectButton'
import { EditCountriesButton } from '../../../../shared/components/buttons/edit-countries-button/EditCountriesButton'
import { FilterChips } from '../../../../shared/components/utility/filter-chips/FilterChips'
import { useSearchState } from '../../../../lib/hooks/useSearchState'
import { useReversedFilters } from '../../../../lib/hooks/useReversedFilters'
import {
  resetFilterFields,
  useRegisterAppliedFilters,
} from '../../../../lib/context/applied-filters/AppliedFiltersProvider'
import { BpCheckbox } from '../../../../shared/components/forms/checkbox/BpCheckbox'
import clsx from 'clsx'
import styles from './DomainOverviewFilters.module.scss'
import {
  GetFilterAdvertiserDomainsApiArg,
  GetFilterKeywordsApiArg,
  LandingFilterArg,
  useGetFilterAdvertiserDomainsQuery,
  useGetFilterAdvertiserLandingQuery,
  useGetFilterCountryQuery,
  useGetFilterEngineQuery,
  useGetFilterKeywordsQuery,
  useGetFilterProjectsQuery,
} from '../../../../app/api'
import { ProjectIcon } from '../../../../shared/components/icons/filters/ProjectIcon'
import { CountryIcon } from '../../../../shared/components/icons/filters/CountryIcon'
import { SearchEngineIcon } from '../../../../shared/components/icons/filters/SearchEngineIcon'
import { KeywordIcon } from '../../../../shared/components/icons/filters/KeywordIcon'
import { UrlIcon } from '../../../../shared/components/icons/filters/UrlIcon'
import { DomainIcon } from '../../../../shared/components/icons/filters/DomainIcon'
import { withSelectedOptions } from '../../../../shared/lib/helpers/WithSelectedOptions'
import { renderCountryOption } from '../../../../shared/components/utility/country-option/CountryOption'
import { restoreAppliedValues } from '../../../../shared/lib/helpers/AppliedValues'

const REVERSED_FILTER_KEYS: Record<string, string> = {
  keyword: 'keyword_id',
  page_url: 'landing_id',
}

/** Фильтры, которые сбрасывает Clear filters, и их значения по умолчанию. */
const CLEARABLE_FILTERS: Partial<IDetailedInfoFilter> = {
  country: '',
  keyword: '',
  page_url: '',
  has_brand: false,
  show_hidden: false,
}

const Filter = ({ searchParams, setSearchParams }: IWithSearchParamsProps<IDetailedInfoFilter>) => {
  const { t } = useTranslation()
  const filters = useAppSelector<IDetailedInfoFilter>(selectDetailedInfoFilters)
  const {
    project,
    site,
    country,
    engine_id,
    dateRange,
    keyword,
    has_brand,
    show_hidden,
    page_url,
    reversed_filters,
  } = filters
  const dispatch = useAppDispatch()
  const [search, setSearch] = useSearchState({ keyword: '', page_url: '', site: '' })

  const {
    getValues,
    setValue,
    control,
    formState: { defaultValues },
  } = useForm<IDetailedInfoFilter>({
    mode: 'onChange',
    defaultValues: {
      project,
      site,
      country,
      engine_id,
      dateRange,
      keyword,
      page_url,
      has_brand,
      show_hidden,
      ...searchParams,
    },
  })

  useEffect(() => {
    applyFilter()
  }, [])

  const {
    data: projects,
    isFetching: projectsFetching,
    isLoading: projectsLoading,
  } = useGetFilterProjectsQuery({})

  const {
    data: countries,
    isFetching: countriesFetching,
    isLoading: countriesLoading,
  } = useGetFilterCountryQuery({ projectId: project ? +project : undefined }, { skip: !project })

  const {
    data: engines,
    isFetching: enginesFetching,
    isLoading: enginesLoading,
  } = useGetFilterEngineQuery({ projectId: project ? +project : undefined }, { skip: !project })

  const {
    data: domains,
    isFetching: domainsFetching,
    isLoading: domainsLoading,
  } = useGetFilterAdvertiserDomainsQuery(
    {
      projectId: project ? +project : undefined,
      search: search.site,
      date: dateRange,
      seo: true,
    } as GetFilterAdvertiserDomainsApiArg,
    { skip: !project },
  )

  const {
    data: landings,
    isFetching: landingsFetching,
    isLoading: landingsLoading,
  } = useGetFilterAdvertiserLandingQuery(
    {
      projectId: project ? +project : undefined,
      search: search.page_url,
      date: dateRange,
    } as LandingFilterArg,
    { skip: !project },
  )

  const {
    data: keywords,
    isFetching: keywordsFetching,
    isLoading: keywordsLoading,
  } = useGetFilterKeywordsQuery(
    {
      projectId: project,
      search: search.keyword,
    } as unknown as GetFilterKeywordsApiArg,
    { skip: !project },
  )

  const applyFilterWithReversed = (reversed: string[]) => {
    const values = { ...getValues(), reversed_filters: reversed.join(',') }
    dispatch(setDetailedInfoFilter(values))
    if (setSearchParams) setSearchParams(values)
  }

  const {
    reversedFilters,
    isReversed,
    isReversedApplied,
    toggleReversedFilter,
    toggleReversedFilterAndApply,
    clearReversedFilter,
    resetReversedFilters,
    restoreReversedFilters,
  } = useReversedFilters({
    keys: REVERSED_FILTER_KEYS,
    applied: reversed_filters,
    fromUrl: searchParams?.reversed_filters,
    onApply: applyFilterWithReversed,
  })

  const applyFilter = () => applyFilterWithReversed(reversedFilters)

  useRegisterAppliedFilters({
    values: filters,
    defaults: CLEARABLE_FILTERS,
    onClear: () => {
      resetFilterFields(setValue, CLEARABLE_FILTERS)
      resetReversedFilters()
      applyFilterWithReversed([])
    },
  })

  return (
    <>
      <form className={clsx(styles.form)}>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => {
              const defaultValue = defaultValues?.project
                ? defaultValues.project
                : project
                  ? project
                  : projects?.length && projects[0].value

              return (
                <BpSelect
                  {...fieldProps}
                  disabled
                  Icon={ProjectIcon}
                  bottomSection={<AddProjectButton />}
                  defaultValue={defaultValue}
                  fieldSize='lg'
                  loading={projectsLoading || projectsFetching}
                  options={projects}
                  title={t('fields.project')}
                  onChange={(e) => {
                    onChange(e)
                    if (e.target.value !== defaultValue) {
                      setValue('country', '')
                    }
                    applyFilter()
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='project'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => (
              <BpSelect
                {...fieldProps}
                disabled
                multiple
                Icon={DomainIcon}
                enableChipLoad={false}
                fieldSize='lg'
                loading={domainsLoading || domainsFetching}
                options={toTextOptions(domains, value)}
                placeholder={t('fields.domain')}
                value={value?.split(',')}
                onChange={(e) => {
                  onChange(
                    [].slice
                      .call(e.target.selectedOptions)
                      .map((o: IOption) => o.value)
                      .join(','),
                  )
                }}
                onDropClose={applyFilter}
                onSearch={setSearch('site')}
              ></BpSelect>
            )}
            control={control}
            name='site'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => (
              <BpSelect
                {...fieldProps}
                Icon={CountryIcon}
                bottomSection={<EditCountriesButton project={{ value: project }} />}
                defaultValue={defaultValues?.country}
                disabled={countriesLoading || countriesFetching}
                fieldSize='lg'
                loading={countriesLoading || countriesFetching}
                optionRender={renderCountryOption}
                options={countries}
                title={t('fields.country')}
                onChange={(e) => {
                  onChange(e)
                  applyFilter()
                }}
              ></BpSelect>
            )}
            control={control}
            name='country'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => {
              const defaultValue = defaultValues?.engine_id
                ? defaultValues.engine_id
                : engine_id
                  ? engine_id
                  : engines && engines[0].value

              return (
                <BpSelect
                  {...fieldProps}
                  Icon={SearchEngineIcon}
                  defaultValue={defaultValue}
                  disabled={enginesLoading || enginesFetching}
                  fieldSize='lg'
                  loading={enginesLoading || enginesFetching}
                  options={engines}
                  title={t('fields.searchEngine')}
                  onChange={(e) => {
                    onChange(e)
                    applyFilter()
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='engine_id'
          />
        </div>
        <div className={clsx(styles.filterDropWrapper)}>
          <FilterDrop
            values={getValues(['dateRange', 'keyword', 'page_url', 'has_brand', 'show_hidden'])}
            onClear={() => {
              setValue('keyword', '')
              setValue('page_url', '')
              resetReversedFilters()
            }}
            onHide={() => {
              restoreAppliedValues(getValues, setValue, filters)
              restoreReversedFilters()
            }}
            onSubmit={applyFilter}
          >
            <Controller
              render={({ field: { onChange, value } }) => {
                return (
                  <BpDatePicker
                    defaultValue={[
                      moment(value, DEFAULT_DATE_FORMAT),
                      moment(value, DEFAULT_RANGE_FORMAT),
                    ]}
                    label={t('fields.date')}
                    onChange={(e, t) => onChange(`${t[0]} - ${t[1]}`)}
                    onOpenChange={(e) => dispatch(setCanCloseDropdown(!e))}
                  />
                )
              }}
              control={control}
              name='dateRange'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('keyword')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('keyword')}
                    />
                  }
                  Icon={KeywordIcon}
                  enableChipLoad={false}
                  label={t('fields.keyword')}
                  loading={keywordsLoading || keywordsFetching}
                  options={withSelectedOptions('keyword', keywords, value)}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                  onSearch={setSearch('keyword')}
                ></BpSelect>
              )}
              control={control}
              name='keyword'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('page_url')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('page_url')}
                    />
                  }
                  Icon={UrlIcon}
                  enableChipLoad={false}
                  label={t('fields.pageUrl')}
                  loading={landingsLoading || landingsFetching}
                  options={withSelectedOptions('landing', landings, value)}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                  onSearch={setSearch('page_url')}
                ></BpSelect>
              )}
              control={control}
              name='page_url'
            />
            <Controller
              render={({ field }) => (
                <div className={clsx(styles.switchRow)}>
                  <span className={clsx(styles.switchLabel)}>{t('fields.hasBrand')}</span>
                  <Switch {...field} defaultChecked={defaultValues?.has_brand} />
                </div>
              )}
              control={control}
              name='has_brand'
            />
            <Controller
              render={({ field }) => (
                <div className={clsx(styles.switchRow)}>
                  <span className={clsx(styles.switchLabel)}>
                    {t('fields.showHiddenAffiliates')}
                  </span>
                  <Switch {...field} defaultChecked={defaultValues?.show_hidden} />
                </div>
              )}
              control={control}
              name='show_hidden'
            />
          </FilterDrop>
        </div>
      </form>
      <div className={clsx(styles.chipsWrapper)}>
        <FilterChips
          applyCallBack={() => {
            resetReversedFilters()
            applyFilterWithReversed([])
          }}
          clearCallBack={(field: any) => {
            setValue(field, '')
            applyFilterWithReversed(clearReversedFilter(field))
          }}
          items={[
            { title: t('fields.date'), field: 'dateRange', value: dateRange, clearable: false },
            {
              title: t('fields.keyword'),
              field: 'keyword',
              value: keyword,
              reversible: true,
              reversed: isReversedApplied('keyword'),
              data: withSelectedOptions('keyword', keywords, keyword),
              type: 'multiselect',
            },
            {
              title: t('fields.pageUrl'),
              field: 'page_url',
              value: page_url,
              reversible: true,
              reversed: isReversedApplied('page_url'),
              data: withSelectedOptions('landing', landings, page_url),
              type: 'multiselect',
            },
          ]}
          reverseCallBack={toggleReversedFilterAndApply}
        />
      </div>
    </>
  )
}

export const DomainOverviewFilters = withSearchParams<
  IWithSearchParamsProps<IDetailedInfoFilter>,
  IDetailedInfoFilter
>(Filter, selectDetailedInfoFilters)
